import {getPropertyKeyIfExists, getTitlePropertyKey} from "@app/core-app/app/domain/notion/inbox";

export type StatusPropertyReport =
  { ok: false } |
  {
    ok: true
    name: string,
    start: string,
    finish: string
  }
export type RecipientPropertyReport =
  { ok: false, type?: string } |
  {
    ok: true
    type: string,
    name: string | null | undefined
  }

export type SendAtPropertyReport =
  { ok: false } |
  {
    ok: true
    name: string | null | undefined
  }

export type SubjectPropertyReport =
  { ok: false } |
  {
    ok: true
    name: string
    type: string
  }

export type CheckboxPropertyReport =
  { ok: false } |
  {
    ok: true
    name: string
  }

export const normalizeStringName = (name: string) => {
  return name.toLowerCase().trim()
}

const statusPropertyParser = (db: any): StatusPropertyReport => {
  const STATUS_SELECT_NAMES = ["email status", "status"]
  const SELECT_INITIAL_STATE = "outbox"
  const SELECT_FINAL_STATE = "sent"
  const properties = Object.keys(db.properties).map(x => normalizeStringName(x))
  const propObjects = db.properties
  const matchingProperty = properties.filter(x => STATUS_SELECT_NAMES.includes(x))

  const reportsForMatchingProps: StatusPropertyReport[] = matchingProperty.map(propertyName => {
    const [originalPropertyKey, value]: [any, any] = Object
      .entries(db?.properties)
      .find(([key, values]) => normalizeStringName(key) === propertyName) || [undefined, {}]

    if (value?.['type'] != "select") {
      return {
        ok: false
      }
    }

    const options = value?.['select']?.['options'] || []
    const matchingStartOption = options.find((o: any) => o.name.toLowerCase() == SELECT_INITIAL_STATE)
    const matchingFinalOption = options.find((o: any) => o.name.toLowerCase() == SELECT_FINAL_STATE)

    if (!matchingStartOption || !matchingFinalOption) {
      return {
        ok: false
      }
    }

    return {
      ok: true,
      name: originalPropertyKey,
      start: matchingStartOption?.name,
      finish: matchingFinalOption?.name
    }
  })

  return reportsForMatchingProps.find(x => x.ok) || {ok: false}

}

const recipientsParser = (db: any, fieldName: string = defaultOutboxFieldNames.recipient): RecipientPropertyReport => {
  const [recipientKey, recipientField]: [any, any] = Object
    .entries(db?.properties)
    .find(([key, values]) => normalizeStringName(key) === normalizeStringName(fieldName)) || [undefined, {}]

  if (!recipientKey) {
    return {
      ok: false,
    }
  }

  const supportedTypes = ["email", "rich_text", "rollup", "formula"]

  if (!supportedTypes.includes(recipientField.type)) {
    return {
      ok: false
    }
  }

  // Validate specific rollup
  const isRollup = recipientField.type === "rollup"
  if (isRollup) {
    const rollup = recipientField.rollup
    if (rollup?.type === "array" && ["show_original", "show_unique"].includes(rollup?.function)) {
      return {
        ok: true,
        type: recipientField.type,
        name: recipientKey
      }
    }
  }

  return {
    ok: true,
    name: recipientKey,
    type: recipientField.type
  }
}
const sendAtParser = (db: any, fieldName: string = defaultOutboxFieldNames.sendAt): SendAtPropertyReport => {
  const sendAtProperty = getPropertyKeyIfExists(db, fieldName, "date")

  if (sendAtProperty) {
    return {
      ok: true,
      name: sendAtProperty
    }
  }

  return {
    ok: false
  }
}


export type OutboxLegacyConfigReport = {
  result: "ok" | "error"
  status: StatusPropertyReport
  recipient: RecipientPropertyReport,
  sendAt: SendAtPropertyReport
  attachments: AttachmentPropertyReport

}


export const outboxLegacyConfigParser = (db: any): OutboxLegacyConfigReport => {
  const status = statusPropertyParser(db)
  const recipient = recipientsParser(db)
  const sendAt = sendAtParser(db)
  const attachments = attachmentsParser(db)

  return {
    result: status.ok && recipient.ok && sendAt.ok ? "ok" : "error",
    status,
    recipient,
    sendAt,
    attachments
  }
}


// New
export type ReadyToSendPropertyReport =
  { ok: false } |
  {
    ok: true
    name: string
  }

export type SentPropertyReport =
  { ok: false } |
  {
    ok: true
    name: string
  }

export type AttachmentPropertyReport =
  { ok: false } |
  {
    ok: true
    name: string
  }

export type InReplyToReport =
  { ok: false } | { ok: true, name: string }


const readyToSendParser = (db: any, fieldName: string = defaultOutboxFieldNames.readyToSend): ReadyToSendPropertyReport => {
  const checkBoxProperty = getPropertyKeyIfExists(db, fieldName, "checkbox")

  if (checkBoxProperty) {
    return {
      ok: true,
      name: checkBoxProperty
    }
  }

  const formulaProperty = getPropertyKeyIfExists(db, fieldName, "formula")

  if (formulaProperty) {
    return {
      ok: true,
      name: formulaProperty
    }
  }

  return {
    ok: false
  }
}

const sentParser = (db: any, fieldName: string = defaultOutboxFieldNames.sent): SentPropertyReport => {
  const checkBoxProperty = getPropertyKeyIfExists(db, fieldName, "checkbox")

  if (checkBoxProperty) {
    return {
      ok: true,
      name: checkBoxProperty
    }
  }

  return {
    ok: false
  }
}


const attachmentsParser = (db: any, fieldName: string = defaultOutboxFieldNames.attachments): AttachmentPropertyReport => {
  const fileProperty = getPropertyKeyIfExists(db, fieldName, "files")

  if (fileProperty) {
    return {
      ok: true,
      name: fileProperty
    }
  }

  return {
    ok: false
  }
}


const inReplyToParser = (db: any, fieldName: string = defaultOutboxFieldNames.inReplyTo): InReplyToReport => {
  const inReplyProperty = getPropertyKeyIfExists(db, fieldName, "relation")

  if (inReplyProperty) {
    return {
      ok: true,
      name: inReplyProperty
    }
  }

  return {
    ok: false
  }
}

const subjectParser = (db: any, fieldName: string | null | undefined): SubjectPropertyReport => {
  if (!fieldName) {
    fieldName = defaultOutboxFieldNames.subject!
  }
  const titleProperty = getPropertyKeyIfExists(db, fieldName, "title")
  if (titleProperty) {
    return {
      ok: true,
      name: titleProperty,
      type: "title"
    }
  }

  const richTextProperty = getPropertyKeyIfExists(db, fieldName, "rich_text")
  if (richTextProperty) {
    return {
      ok: true,
      name: richTextProperty,
      type: "rich_text"
    }
  }

  const titlePropertyDefault = getTitlePropertyKey(db)
  if (titlePropertyDefault) {
    return {
      ok: true,
      name: titlePropertyDefault,
      type: "title"
    }
  }

  return {
    ok: false
  }

}

const errorParser = (db: any, fieldName: string | null | undefined): CheckboxPropertyReport => {
//   Check if checkbox field exists
  if (!fieldName) {
    fieldName = defaultOutboxFieldNames.error!
  }
  const checkboxProperty = getPropertyKeyIfExists(db, fieldName, "checkbox")

  if (checkboxProperty) {
    return {
      ok: true,
      name: checkboxProperty
    }
  }
  return {
    ok: false
  }
}
export type OutboxV2ConfigReport = {
  result: "ok" | "error"
  subject: SubjectPropertyReport
  recipient: RecipientPropertyReport,
  sendAt: SendAtPropertyReport,
  readyToSend: ReadyToSendPropertyReport,
  sent: SentPropertyReport
  attachments: AttachmentPropertyReport
  inReplyTo: InReplyToReport
  error: CheckboxPropertyReport

}

export type OutboxFieldNames = {
  subject: string | undefined | null;
  recipient: string;
  sendAt: string;
  readyToSend: string;
  sent: string;
  attachments: string;
  inReplyTo: string;
  error: string;
};

export const defaultOutboxFieldNames: OutboxFieldNames = {
  subject: "name",
  recipient: "recipient",
  sendAt: "send at",
  readyToSend: "ready to send",
  sent: "sent",
  attachments: "attachments",
  inReplyTo: "in reply to",
  error: "error"
};


export const outboxV2ConfigParser = (db: any, customFieldNames: OutboxFieldNames | undefined | null): OutboxV2ConfigReport => {
  const effectiveFieldNames = {...defaultOutboxFieldNames, ...(customFieldNames || {})};

  const subject = subjectParser(db, effectiveFieldNames.subject);
  const recipient = recipientsParser(db, effectiveFieldNames.recipient);
  const sendAt = sendAtParser(db, effectiveFieldNames.sendAt);
  const readyToSend = readyToSendParser(db, effectiveFieldNames.readyToSend);
  const sent = sentParser(db, effectiveFieldNames.sent);
  const attachments = attachmentsParser(db, effectiveFieldNames.attachments);
  const inReplyTo = inReplyToParser(db, effectiveFieldNames.inReplyTo);
  const error = errorParser(db, effectiveFieldNames.error);

  return {
    result: subject.ok && recipient.ok && sendAt.ok && readyToSend.ok && sent.ok ? "ok" : "error",
    subject,
    recipient,
    sendAt,
    readyToSend,
    sent,
    attachments,
    inReplyTo,
    error
  }
}



